export interface IEhrCsvMedication {
  medicationName: string | null;
  dosage: string | null;
  route: string | null;
  frequency: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string | null;
}

export interface IParseResult {
  data: {
    medications: IEhrCsvMedication[];
  } | null;
  errors: string[];
  skippedRowsErrors: string[];
}

const REQUIRED_COLUMNS = [
  'MedicationName',
  'Dosage',
  'Route',
  'Frequency',
  'StartDate',
  'EndDate',
  'Status',
];

const COLUMN_MAP: Record<string, keyof IEhrCsvMedication> = {
  MedicationName: 'medicationName',
  Dosage: 'dosage',
  Route: 'route',
  Frequency: 'frequency',
  StartDate: 'startDate',
  EndDate: 'endDate',
  Status: 'status',
};

const DANGEROUS_CHARS = ['=', '+', '@', '\t', '\r'];

export function parseCSVRow(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

/**
 * Sanitize input to prevent CSV injection attacks
 * Removes leading dangerous characters that could be interpreted as formulas
 */
function sanitizeCell(value: string): string {
  if (!value) return value;

  let sanitized = value.trim();

  while (sanitized.length > 0 && DANGEROUS_CHARS.includes(sanitized[0])) {
    sanitized = sanitized.substring(1);
  }

  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '');

  return sanitized.trim();
}

function normalizeHeader(header: string) {
  return header.trim().toLowerCase();
}

export function validateAndParseEhrCsv(csvText: string): IParseResult {
  const errors: string[] = [];
  const skippedRowsErrors: string[] = [];

  const lines = csvText.split(/\r?\n/).filter((line) => line.trim().length > 0);

  // Guard against completely empty files
  if (lines.length === 0) {
    return {
      data: null,
      errors: ['labels.invalidCsvFormat'],
      skippedRowsErrors: [],
    };
  }

  // Parse headers
  const rawHeaders = parseCSVRow(lines[0]);
  const headers = rawHeaders.map(normalizeHeader);

  // Map required columns to their indices
  const colIndices: Record<string, number> = {};
  for (const col of REQUIRED_COLUMNS) {
    const idx = headers.findIndex((h) => h === normalizeHeader(col));
    if (idx === -1) {
      return {
        data: null,
        errors: ['labels.invalidCsvFormat'],
        skippedRowsErrors: [],
      };
    }
    colIndices[col] = idx;
  }

  // Guard against files with only headers (no data rows)
  if (lines.length < 2) {
    return {
      data: null,
      errors: ['labels.noMedicationRecords'],
      skippedRowsErrors: [],
    };
  }

  // Parse rows - First pass: identify duplicates
  const medicationNameCounts = new Map<string, number>();
  const allRows: Array<{ row: string[]; index: number }> = [];
  let totalDataRows = 0;

  for (let i = 1; i < lines.length; i++) {
    const row = parseCSVRow(lines[i]);
    totalDataRows++;
    // Skip empty lines
    if (row.every((cell) => cell.trim() === '')) {
      // skip empty line
    } else {
      allRows.push({ row, index: i });

      // Count medication names (sanitized)
      const medNameIdx = colIndices.MedicationName;
      const rawMedName =
        row[medNameIdx] !== undefined ? row[medNameIdx].trim() : '';
      const medName = sanitizeCell(rawMedName);

      if (medName) {
        const normalizedName = medName.toLowerCase();
        medicationNameCounts.set(
          normalizedName,
          (medicationNameCounts.get(normalizedName) || 0) + 1,
        );
      }
    }
  }

  // Second pass: parse only non-duplicate rows with sanitization
  const medications: IEhrCsvMedication[] = [];
  let skippedRowsCount = 0;
  let duplicateRowsCount = 0;

  for (const { row } of allRows) {
    const med: IEhrCsvMedication = {
      medicationName: null,
      dosage: null,
      route: null,
      frequency: null,
      startDate: null,
      endDate: null,
      status: null,
    };

    for (const col of REQUIRED_COLUMNS) {
      const idx = colIndices[col];
      const rawValue = row[idx] !== undefined ? row[idx].trim() : '';
      // Sanitize all input to prevent CSV injection
      const sanitizedValue = sanitizeCell(rawValue);

      if (col === 'MedicationName') {
        med.medicationName = sanitizedValue || null;
      } else {
        med[COLUMN_MAP[col]] = sanitizedValue === '' ? null : sanitizedValue;
      }
    }

    // Skip row if MedicationName is empty
    if (!med.medicationName) {
      skippedRowsCount++;
    } else if (
      (medicationNameCounts.get(med.medicationName.toLowerCase()) ?? 0) > 1
    ) {
      // Skip if this medication name appears more than once (all instances)
      duplicateRowsCount++;
    } else {
      medications.push(med);
    }
  }

  if (skippedRowsCount > 0) {
    skippedRowsErrors.push(`${skippedRowsCount} missing Medication Name`);
  }

  if (duplicateRowsCount > 0) {
    skippedRowsErrors.push(`${duplicateRowsCount} duplicate rows`);
  }

  // Check if all rows were skipped (no valid medications)
  if (medications.length === 0 && totalDataRows > 0) {
    return {
      data: null,
      errors: ['labels.invalidCsvFormat'],
      skippedRowsErrors: [],
    };
  }

  return {
    data: {
      medications,
    },
    errors,
    skippedRowsErrors,
  };
}
